import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Clock, Search, Filter, Plus, MapPin, MessageCircle, CheckCircle, Heart,
  HandHeart, User, Trash2, X, Briefcase, Share2, Pencil, Camera
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ShareButtons from '../components/ShareButtons';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TimeCard = ({ offer, language, onContact, onEdit, onDelete, isAuthenticated, currentUserId }) => {
  const isOffer = offer.type === 'offer';
  const isOwner = currentUserId === offer.user_id;
  const shareUrl = `${API_URL}/api/share/time/${offer.offer_id}`;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-stone-800 rounded-2xl shadow-sm border border-stone-200 dark:border-stone-700 overflow-hidden card-lift"
      data-testid={`time-offer-${offer.offer_id}`}>
      {/* Cover image */}
      {offer.images?.length > 0 && (
        <Link to={`/donate-time/${offer.offer_id}`}>
        <div className="aspect-[3/1] overflow-hidden bg-stone-100 relative">
          <img src={offer.images[0]} alt={offer.title} className="w-full h-full object-cover" />
          {isOwner && (
            <div className="absolute top-2 right-2 flex gap-1.5">
              <button onClick={() => onEdit(offer)} data-testid={`edit-time-${offer.offer_id}`}
                className="p-2 bg-white/90 backdrop-blur-sm rounded-full shadow hover:bg-white transition-colors">
                <Pencil className="h-3.5 w-3.5 text-stone-600" />
              </button>
              <button onClick={() => onDelete(offer)} data-testid={`delete-time-${offer.offer_id}`}
                className="p-2 bg-white/90 backdrop-blur-sm rounded-full shadow hover:bg-red-50 transition-colors">
                <Trash2 className="h-3.5 w-3.5 text-red-500" />
              </button>
            </div>
          )}
        </div>
        </Link>
      )}
      <div className="p-5">
        <div className="flex items-start gap-4">
          <Link to={`/profile/${offer.user_id}`}>
            <Avatar className="h-12 w-12 shrink-0 ring-2 ring-stone-100 dark:ring-stone-700">
              <AvatarImage src={offer.user_picture || `https://api.dicebear.com/9.x/lorelei/svg?seed=${offer.user_id}`} />
              <AvatarFallback className="bg-amber-100 text-amber-700">{offer.user_name?.[0]}</AvatarFallback>
            </Avatar>
          </Link>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <Link to={`/profile/${offer.user_id}`} className="font-semibold text-stone-900 dark:text-white hover:text-amber-700 transition-colors">
                {offer.user_name}
              </Link>
              {offer.is_verified && <CheckCircle className="h-4 w-4 text-blue-500" />}
            </div>
            <div className="flex items-center gap-1.5 flex-wrap mb-2">
              <Badge className={isOffer ? 'bg-violet-100 text-violet-700 text-xs' : 'bg-rose-100 text-rose-700 text-xs'}>
                {isOffer ? (language === 'it' ? 'Offro tempo' : 'Offering time') : (language === 'it' ? 'Cerco aiuto' : 'Need help')}
              </Badge>
              <Badge variant="outline" className="text-xs"><MapPin className="h-3 w-3 mr-0.5" />{offer.city}</Badge>
              <Badge variant="secondary" className="text-xs">{offer.category_name || offer.category}</Badge>
            </div>
            <Link to={`/donate-time/${offer.offer_id}`}>
              <h3 className="font-heading font-semibold text-stone-900 dark:text-white mb-1 hover:text-violet-700 transition-colors">{offer.title}</h3>
            </Link>
            <p className="text-stone-500 dark:text-stone-400 text-sm line-clamp-3 mb-2">{offer.description}</p>
            {offer.skills && (
              <div className="flex items-center gap-1.5 text-xs text-stone-500 mb-2">
                <Briefcase className="h-3.5 w-3.5 shrink-0" />
                <span className="line-clamp-1">{offer.skills}</span>
              </div>
            )}
            {offer.availability && (
              <div className="flex items-center gap-1.5 text-xs text-stone-500 mb-3">
                <Clock className="h-3.5 w-3.5 shrink-0" />
                <span>{offer.availability}</span>
              </div>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              {isAuthenticated && !isOwner && (
                <Button size="sm" onClick={() => onContact(offer)} data-testid={`contact-time-${offer.offer_id}`}
                  className="bg-violet-700 hover:bg-violet-800 text-white rounded-full text-xs px-4">
                  <MessageCircle className="h-3.5 w-3.5 mr-1" />{language === 'it' ? 'Chatta' : 'Chat'}
                </Button>
              )}
              {isOwner && !offer.images?.length && (
                <>
                  <Button size="sm" variant="ghost" onClick={() => onEdit(offer)} data-testid={`edit-time-${offer.offer_id}`}
                    className="text-stone-500 hover:bg-stone-100 rounded-full text-xs">
                    <Pencil className="h-3.5 w-3.5 mr-1" />{language === 'it' ? 'Modifica' : 'Edit'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => onDelete(offer)} data-testid={`delete-time-${offer.offer_id}`}
                    className="text-red-500 hover:bg-red-50 rounded-full text-xs">
                    <Trash2 className="h-3.5 w-3.5 mr-1" />{language === 'it' ? 'Elimina' : 'Delete'}
                  </Button>
                </>
              )}
              <div className="ml-auto scale-75 origin-right">
                <ShareButtons url={shareUrl} title={offer.title} description={offer.description} language={language} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const CreateTimeForm = ({ language, token, categories, onSuccess, editItem }) => {
  const [type, setType] = useState(editItem?.type || 'offer');
  const [title, setTitle] = useState(editItem?.title || '');
  const [description, setDescription] = useState(editItem?.description || '');
  const [skills, setSkills] = useState(editItem?.skills || '');
  const [category, setCategory] = useState(editItem?.category || '');
  const [city, setCity] = useState(editItem?.city || '');
  const [availability, setAvailability] = useState(editItem?.availability || '');
  const [images, setImages] = useState(editItem?.images || []);
  const [loading, setLoading] = useState(false);
  const imageInputRef = useRef(null);
  const it = language === 'it';
  const isEditing = !!editItem;

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (images.length + files.length > 5) { toast.error(it ? 'Massimo 5 immagini' : 'Maximum 5 images'); return; }
    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) return;
      const reader = new FileReader();
      reader.onloadend = () => setImages(prev => [...prev, reader.result]);
      reader.readAsDataURL(file);
    });
  };

  const handleSubmit = async () => {
    if (!title.trim() || !description.trim() || !city.trim() || !category) {
      toast.error(it ? 'Compila tutti i campi obbligatori' : 'Fill all required fields');
      return;
    }
    if (images.length === 0) {
      toast.error(it ? 'Aggiungi almeno un\'immagine di copertina' : 'Add at least one cover image');
      return;
    }
    setLoading(true);
    try {
      const data = {
        type, title: title.trim(), description: description.trim(),
        skills: skills.trim(), category, city: city.trim(), availability: availability.trim(),
        images
      };
      if (isEditing) {
        await axios.put(`${API_URL}/api/time/offers/${editItem.offer_id}`, data,
          { headers: { Authorization: `Bearer ${token}` } });
        toast.success(it ? 'Annuncio aggiornato!' : 'Listing updated!');
      } else {
        await axios.post(`${API_URL}/api/time/offers`, data,
          { headers: { Authorization: `Bearer ${token}` } });
        toast.success(it ? 'Annuncio pubblicato!' : 'Listing published!');
      }
      onSuccess();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); } finally { setLoading(false); }
  };

  return (
    <div className="space-y-5">
      {!isEditing && (
        <div className="grid grid-cols-2 gap-3">
          <button onClick={() => setType('offer')} data-testid="time-type-offer"
            className={`p-4 rounded-xl border-2 text-center transition-all ${type === 'offer' ? 'border-violet-500 bg-violet-50' : 'border-stone-200'}`}>
            <Heart className={`h-6 w-6 mx-auto mb-2 ${type === 'offer' ? 'text-violet-600' : 'text-stone-400'}`} />
            <p className={`font-semibold text-sm ${type === 'offer' ? 'text-violet-700' : 'text-stone-600'}`}>
              {it ? 'Offro il mio tempo' : 'I offer my time'}
            </p>
          </button>
          <button onClick={() => setType('request')} data-testid="time-type-request"
            className={`p-4 rounded-xl border-2 text-center transition-all ${type === 'request' ? 'border-rose-500 bg-rose-50' : 'border-stone-200'}`}>
            <HandHeart className={`h-6 w-6 mx-auto mb-2 ${type === 'request' ? 'text-rose-600' : 'text-stone-400'}`} />
            <p className={`font-semibold text-sm ${type === 'request' ? 'text-rose-700' : 'text-stone-600'}`}>
              {it ? 'Ho bisogno di aiuto' : 'I need help'}
            </p>
          </button>
        </div>
      )}

      <div className="space-y-2">
        <Label className="font-semibold">{it ? 'Titolo *' : 'Title *'}</Label>
        <Input data-testid="time-title" value={title} onChange={e => setTitle(e.target.value)}
          placeholder={type === 'offer' ? (it ? 'Es: Ripetizioni di matematica gratis' : 'E.g.: Free math tutoring') : (it ? 'Es: Cerco aiuto per trasloco' : 'E.g.: Need help with moving')}
          className="h-12 rounded-xl" />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label className="font-semibold">{it ? 'Categoria *' : 'Category *'}</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="time-category" className="h-12 rounded-xl"><SelectValue placeholder={it ? 'Seleziona' : 'Select'} /></SelectTrigger>
            <SelectContent>
              {categories.map(c => <SelectItem key={c.id} value={c.id}>{language === 'it' ? c.name_it : c.name_en}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="font-semibold"><MapPin className="h-3.5 w-3.5 inline mr-1" />{it ? 'Città *' : 'City *'}</Label>
          <Input data-testid="time-city" value={city} onChange={e => setCity(e.target.value)}
            placeholder={it ? 'Es: Roma, Milano...' : 'E.g.: Rome, Milan...'} className="h-12 rounded-xl" />
        </div>
      </div>

      <div className="space-y-2">
        <Label className="font-semibold">{it ? 'Descrizione *' : 'Description *'}</Label>
        <Textarea data-testid="time-description" value={description} onChange={e => setDescription(e.target.value)}
          placeholder={it ? 'Presentati: cosa sai fare e cosa offri...' : 'Introduce yourself: what you can do and what you offer...'}
          className="min-h-[120px] rounded-xl" />
      </div>

      <div className="space-y-2">
        <Label className="font-semibold">{it ? 'Competenze' : 'Skills'}</Label>
        <Input data-testid="time-skills" value={skills} onChange={e => setSkills(e.target.value)}
          placeholder={it ? 'Es: Idraulico, Insegnante, Cuoco...' : 'E.g.: Plumber, Teacher, Cook...'} className="h-12 rounded-xl" />
      </div>

      <div className="space-y-2">
        <Label className="font-semibold"><Clock className="h-3.5 w-3.5 inline mr-1" />{it ? 'Disponibilità' : 'Availability'}</Label>
        <Input data-testid="time-availability" value={availability} onChange={e => setAvailability(e.target.value)}
          placeholder={it ? 'Es: Lunedì-Venerdì ore 9-12' : 'E.g.: Mon-Fri 9am-12pm'} className="h-12 rounded-xl" />
      </div>

      {/* Photos */}
      <div className="space-y-2">
        <Label className="font-semibold">{it ? 'Foto di copertina * (max 5)' : 'Cover photos * (max 5)'}</Label>
        <input type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" ref={imageInputRef} />
        <div className="flex flex-wrap gap-3">
          {images.map((img, i) => (
            <div key={i} className="relative group">
              <img src={img} alt="" className="h-20 w-20 object-cover rounded-xl border-2 border-stone-200" />
              <button type="button" onClick={() => setImages(prev => prev.filter((_, idx) => idx !== i))}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
          {images.length < 5 && (
            <button type="button" onClick={() => imageInputRef.current?.click()}
              className="h-20 w-20 rounded-xl border-2 border-dashed border-stone-300 flex flex-col items-center justify-center text-stone-400 hover:border-violet-500 hover:text-violet-600 transition-colors">
              <Camera className="h-5 w-5 mb-1" /><span className="text-[10px]">{it ? 'Aggiungi' : 'Add'}</span>
            </button>
          )}
        </div>
      </div>

      <Button onClick={handleSubmit} disabled={loading} data-testid="time-publish-btn"
        className="w-full h-12 bg-violet-700 hover:bg-violet-800 text-white rounded-xl font-semibold">
        {loading ? '...' : isEditing ? (it ? 'Salva modifiche' : 'Save changes') : (it ? 'Pubblica annuncio' : 'Publish listing')}
      </Button>
    </div>
  );
};

const DonateTime = () => {
  const { isAuthenticated, user, token } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const highlightItemId = searchParams.get('item');
  const [offers, setOffers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const it = language === 'it';

  useEffect(() => { fetchCategories(); }, []);
  useEffect(() => { fetchOffers(); }, [activeTab, selectedCategory]);

  useEffect(() => {
    if (highlightItemId && !loading) {
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="time-offer-${highlightItemId}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.classList.add('ring-2', 'ring-violet-500', 'ring-offset-2');
          setTimeout(() => el.classList.remove('ring-2', 'ring-violet-500', 'ring-offset-2'), 4000);
        }
      }, 300);
    }
  }, [highlightItemId, loading]);

  const fetchCategories = async () => {
    try { const res = await axios.get(`${API_URL}/api/time/categories`); setCategories(res.data.categories); } catch {}
  };

  const fetchOffers = async () => {
    setLoading(true);
    try {
      const params = { limit: 50 };
      if (activeTab !== 'all') params.type = activeTab;
      if (selectedCategory !== 'all') params.category = selectedCategory;
      if (searchQuery.trim()) params.search = searchQuery.trim();
      if (cityFilter.trim()) params.city = cityFilter.trim();
      const res = await axios.get(`${API_URL}/api/time/offers`, { params });
      setOffers(res.data.offers);
      setTotal(res.data.total);
    } catch {} finally { setLoading(false); }
  };

  const getCatName = (id) => { const c = categories.find(x => x.id === id); return c ? (it ? c.name_it : c.name_en) : id; };

  const handleContact = async (offer) => {
    try {
      const res = await axios.post(`${API_URL}/api/time/offers/${offer.offer_id}/contact`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(it ? 'Messaggio inviato!' : 'Message sent!');
      navigate('/chat', { state: { conversationId: res.data.conversation_id } });
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleDelete = async (offer) => {
    if (!window.confirm(it ? 'Eliminare questo annuncio?' : 'Delete this listing?')) return;
    try {
      await axios.delete(`${API_URL}/api/time/offers/${offer.offer_id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(it ? 'Eliminato' : 'Deleted');
      fetchOffers();
    } catch { toast.error('Error'); }
  };

  const enriched = offers.map(o => ({ ...o, category_name: getCatName(o.category) }));

  return (
    <div className="min-h-screen bg-stone-100 dark:bg-stone-950 overflow-y-auto">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-16">
        {/* Hero */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-violet-700 to-purple-800 rounded-3xl p-6 md:p-10 mb-8 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-48 h-48 bg-violet-600/30 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <Clock className="h-7 w-7" />
                <span className="font-medium text-violet-200">{it ? 'Dona il tuo Tempo' : 'Donate your Time'}</span>
              </div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold mb-2" data-testid="donate-time-title">
                {it ? 'Il tempo è il regalo più prezioso' : 'Time is the most precious gift'}
              </h1>
              <p className="text-violet-100 max-w-lg">
                {it ? 'Offri le tue competenze a chi ne ha bisogno o trova qualcuno che può aiutarti. Tutto gratuito, tutto nella tua città.' : 'Offer your skills to those in need or find someone who can help you. All free, all in your city.'}
              </p>
            </div>
            {isAuthenticated && (
              <Dialog open={showCreate} onOpenChange={setShowCreate}>
                <DialogTrigger asChild>
                  <Button data-testid="create-time-btn" className="bg-white text-violet-800 hover:bg-violet-50 rounded-full px-6 py-3 font-semibold shadow-lg shrink-0">
                    <Plus className="h-5 w-5 mr-2" />{it ? 'Pubblica annuncio' : 'Create listing'}
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader><DialogTitle className="font-heading text-xl">{it ? 'Dona il tuo tempo' : 'Donate your time'}</DialogTitle></DialogHeader>
                  <CreateTimeForm language={language} token={token} categories={categories}
                    onSuccess={() => { setShowCreate(false); fetchOffers(); }} />
                </DialogContent>
              </Dialog>
            )}
          </div>
        </motion.div>

        {/* Filters */}
        <div className="bg-white dark:bg-stone-800 rounded-2xl shadow-sm border border-stone-200 dark:border-stone-700 p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-3">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full md:w-auto">
              <TabsList className="grid grid-cols-3 w-full md:w-auto">
                <TabsTrigger value="all" data-testid="time-tab-all">{it ? 'Tutti' : 'All'}</TabsTrigger>
                <TabsTrigger value="offer" data-testid="time-tab-offers"><Heart className="h-3.5 w-3.5 mr-1" />{it ? 'Offrono' : 'Offering'}</TabsTrigger>
                <TabsTrigger value="request" data-testid="time-tab-requests"><HandHeart className="h-3.5 w-3.5 mr-1" />{it ? 'Cercano' : 'Seeking'}</TabsTrigger>
              </TabsList>
            </Tabs>
            <form onSubmit={e => { e.preventDefault(); fetchOffers(); }} className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
              <input data-testid="time-search" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                placeholder={it ? 'Cerca competenze...' : 'Search skills...'}
                className="w-full pl-9 pr-4 py-2.5 bg-stone-50 dark:bg-stone-700 dark:text-white rounded-xl text-sm border border-stone-200 dark:border-stone-600 focus:outline-none focus:ring-2 focus:ring-violet-500/20" />
            </form>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
              <input data-testid="time-city-filter" value={cityFilter}
                onChange={e => setCityFilter(e.target.value)}
                onBlur={fetchOffers} onKeyDown={e => e.key === 'Enter' && fetchOffers()}
                placeholder={it ? 'Filtra per città...' : 'Filter by city...'}
                className="w-full md:w-[180px] pl-9 pr-4 py-2.5 bg-stone-50 dark:bg-stone-700 dark:text-white rounded-xl text-sm border border-stone-200 dark:border-stone-600 focus:outline-none focus:ring-2 focus:ring-violet-500/20" />
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger data-testid="time-category-filter" className="w-full md:w-[180px] rounded-xl">
                <Filter className="h-4 w-4 mr-2 text-stone-400" /><SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{it ? 'Tutte le categorie' : 'All categories'}</SelectItem>
                {categories.map(c => <SelectItem key={c.id} value={c.id}>{it ? c.name_it : c.name_en}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <p className="text-stone-600 dark:text-stone-400 text-sm mb-4">{total} {it ? 'annunci trovati' : 'listings found'}</p>

        {loading ? (
          <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-700" /></div>
        ) : enriched.length === 0 ? (
          <div className="text-center py-20">
            <Clock className="h-16 w-16 text-stone-300 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-semibold text-stone-700 dark:text-stone-300 mb-2" data-testid="no-time-offers">
              {it ? 'Nessun annuncio trovato' : 'No listings found'}
            </h3>
            <p className="text-stone-500 mb-6">{it ? 'Sii il primo a donare il tuo tempo!' : 'Be the first to donate your time!'}</p>
            {isAuthenticated && (
              <Button onClick={() => setShowCreate(true)} className="bg-violet-700 hover:bg-violet-800 text-white rounded-full px-6">
                <Plus className="h-5 w-5 mr-2" />{it ? 'Pubblica annuncio' : 'Create listing'}
              </Button>
            )}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-5">
            {enriched.map(offer => (
              <TimeCard key={offer.offer_id} offer={offer} language={language}
                onContact={handleContact} onEdit={setEditingOffer} onDelete={handleDelete}
                isAuthenticated={isAuthenticated} currentUserId={user?.user_id} />
            ))}
          </div>
        )}

        {/* Edit Dialog */}
        <Dialog open={!!editingOffer} onOpenChange={(open) => { if (!open) setEditingOffer(null); }}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">{it ? 'Modifica annuncio' : 'Edit listing'}</DialogTitle>
            </DialogHeader>
            {editingOffer && (
              <CreateTimeForm language={language} token={token} categories={categories} editItem={editingOffer}
                onSuccess={() => { setEditingOffer(null); fetchOffers(); }} />
            )}
          </DialogContent>
        </Dialog>
      </main>
      <Footer />
    </div>
  );
};

export default DonateTime;
